import React, { useCallback, useEffect, useState } from 'react';
import { PlusIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/outline';
import { absencesAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';
import { ABSENCE_TYPE_LABELS, AbsenceType, PlayerAbsence } from '../../types/player';
import { absenceBadgeClass, describeAbsence, toDateInputValue } from '../../utils/absence';

interface AbsencePanelProps {
  playerId: number;
  playerName: string;
  /** Változás után (pl. a lista jelvényeinek frissítéséhez). */
  onChanged?: () => void;
  /** Rögtön a bejelentő űrlappal nyíljon (pl. a kezdőlap "Hiányzás bejelentése" sorából). */
  startOpen?: boolean;
}

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Betegség / sérülés bejelentése időszakra. A bejelentés az időszakba eső
 * eseményekre automatikusan "Nem jön"-t ad, így nem kell eseményenként lemondani.
 */
const AbsencePanel: React.FC<AbsencePanelProps> = ({ playerId, playerName, onChanged, startOpen = false }) => {
  const { showToast } = useToast();
  const [absences, setAbsences] = useState<PlayerAbsence[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [formOpen, setFormOpen] = useState(startOpen);
  const [saving, setSaving] = useState(false);

  const today = toDateInputValue(new Date());
  const [type, setType] = useState<AbsenceType>(AbsenceType.Illness);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      setAbsences(await absencesAPI.getForPlayer(playerId));
    } catch (err) {
      console.error('A hiányzások betöltése nem sikerült:', err);
    } finally {
      setLoaded(true);
    }
  }, [playerId]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setType(AbsenceType.Illness);
    setStartDate(today);
    setEndDate(today);
    setNote('');
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const created = await absencesAPI.create(playerId, { type, startDate, endDate, note: note.trim() || undefined });
      showToast(
        created.affectedEventCount > 0
          ? `${playerName}: bejelentve, ${created.affectedEventCount} eseményről automatikusan lejelentve.`
          : 'Bejelentve. Az időszakban jelenleg nincs esemény; ha lesz, automatikusan lejelentjük.'
      );
      setFormOpen(false);
      resetForm();
      await load();
      onChanged?.();
    } catch (err) {
      showToast(getErrorMessage(err, 'A bejelentés nem sikerült.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  /** Korábbi gyógyulás: a vége mára kerül, a további események visszaállnak. */
  const handleRecovered = async (absence: PlayerAbsence) => {
    setSaving(true);
    try {
      await absencesAPI.update(absence.id, {
        type: absence.type,
        startDate: toDateInputValue(absence.startDate),
        endDate: today < toDateInputValue(absence.startDate) ? toDateInputValue(absence.startDate) : today,
        note: absence.note ?? undefined,
      });
      showToast('Rendben, a holnaptól kezdődő eseményekre újra lehet jelezni.');
      await load();
      onChanged?.();
    } catch (err) {
      showToast(getErrorMessage(err, 'A módosítás nem sikerült.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (absence: PlayerAbsence) => {
    if (!window.confirm('Törlöd a bejelentést? Az érintett eseményeken a "Nem jön" visszaáll.')) return;
    setSaving(true);
    try {
      await absencesAPI.delete(absence.id);
      showToast('Bejelentés törölve.');
      await load();
      onChanged?.();
    } catch (err) {
      showToast(getErrorMessage(err, 'A törlés nem sikerült.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const active = absences.filter((a) => toDateInputValue(a.endDate) >= today);
  const past = absences.filter((a) => toDateInputValue(a.endDate) < today).slice(0, 3);

  return (
    <div className="space-y-3">
      {!loaded ? (
        <p className="text-sm text-gray-400">Betöltés...</p>
      ) : (
        <>
          {active.length === 0 && !formOpen && (
            <p className="text-sm text-gray-500">Nincs folyamatban lévő betegség vagy sérülés.</p>
          )}

          {active.map((absence) => (
            <div key={absence.id} className="rounded-lg border border-gray-200 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${absenceBadgeClass(absence.type)}`}>
                  {describeAbsence(absence)}
                </span>
                {absence.affectedEventCount > 0 && (
                  <span className="text-xs text-gray-500">{absence.affectedEventCount} esemény lejelentve</span>
                )}
              </div>
              {absence.note && <p className="mt-1 text-sm text-gray-700">{absence.note}</p>}
              <p className="mt-1 text-xs text-gray-400">Bejelentette: {absence.createdByUserName}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {toDateInputValue(absence.endDate) > today && (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => handleRecovered(absence)}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <CheckIcon className="h-4 w-4" />
                    Meggyógyult
                  </button>
                )}
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => handleDelete(absence)}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <TrashIcon className="h-4 w-4" />
                  Törlés
                </button>
              </div>
            </div>
          ))}

          {formOpen ? (
            <form onSubmit={handleCreate} className="space-y-3 rounded-lg border border-primary-200 bg-primary-50/40 p-3">
              <div className="flex flex-wrap gap-2">
                {[AbsenceType.Illness, AbsenceType.Injury, AbsenceType.Other].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                      type === t ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-300'
                    }`}
                  >
                    {ABSENCE_TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm text-gray-700">
                  Ettől
                  <input
                    type="date"
                    required
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      if (e.target.value > endDate) setEndDate(e.target.value);
                    }}
                    className={`mt-1 ${inputClass}`}
                  />
                </label>
                <label className="text-sm text-gray-700">
                  Eddig (ezt a napot is beleértve)
                  <input
                    type="date"
                    required
                    min={startDate}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`mt-1 ${inputClass}`}
                  />
                </label>
              </div>
              <label className="block text-sm text-gray-700">
                Megjegyzés (nem kötelező)
                <input
                  type="text"
                  maxLength={500}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Pl. bokaficam, orvosi igazolás jövő héten"
                  className={`mt-1 ${inputClass}`}
                />
              </label>
              <p className="text-xs text-gray-500">
                Az időszakba eső edzésekre és meccsekre automatikusan „Nem jön” kerül, és az edző értesítést kap.
              </p>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setFormOpen(false);
                    resetForm();
                  }}
                  className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? 'Mentés...' : 'Bejelentés'}
                </button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4" />
              Betegség / sérülés bejelentése
            </button>
          )}

          {past.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500">Korábbiak</p>
              <ul className="mt-1 space-y-1">
                {past.map((a) => (
                  <li key={a.id} className="text-xs text-gray-500">
                    {describeAbsence(a)}
                    {a.note ? ` – ${a.note}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AbsencePanel;
